import api from '../services/api';
import { createImageThumbnail, isGif, shouldCacheThumbnailOnly } from './thumbnailGenerator';
import * as encryption from '../services/encryption';

// Unified cache for all blob URLs (avatars, attachments, images)
interface CacheEntry {
  blobUrl: string;
  timestamp: number;
  accessCount: number;
  isThumbnail?: boolean; // Track if this is a thumbnail or full image
}

const blobCache = new Map<string, CacheEntry>();
const pendingRequests = new Map<string, Promise<string>>();

// Cache configuration
const CACHE_CONFIG = {
  maxEntries: 200, // Maximum cached blobs
  maxAge: 30 * 60 * 1000, // 30 minutes TTL
  cleanupInterval: 5 * 60 * 1000, // Cleanup every 5 minutes
};

let cleanupTimer: NodeJS.Timeout | null = null;

/**
 * Start automatic cache cleanup
 */
function startCacheCleanup(): void {
  if (cleanupTimer) return;

  cleanupTimer = setInterval(() => {
    const now = Date.now();
    const keysToDelete: string[] = [];

    // Remove expired entries
    for (const [key, entry] of blobCache.entries()) {
      if (now - entry.timestamp > CACHE_CONFIG.maxAge) {
        keysToDelete.push(key);
      }
    }

    // Clean up expired entries
    keysToDelete.forEach((key) => {
      const entry = blobCache.get(key);
      if (entry) {
        URL.revokeObjectURL(entry.blobUrl);
        blobCache.delete(key);
      }
    });

    // If still over limit, evict least accessed entries
    if (blobCache.size > CACHE_CONFIG.maxEntries) {
      evictLRU();
    }
  }, CACHE_CONFIG.cleanupInterval);
}

/**
 * Evict least recently used entries
 */
function evictLRU(): void {
  const entries = Array.from(blobCache.entries())
    .sort((a, b) => a[1].accessCount - b[1].accessCount)
    .slice(0, Math.floor(CACHE_CONFIG.maxEntries * 0.2)); // Remove 20%

  entries.forEach(([key, entry]) => {
    URL.revokeObjectURL(entry.blobUrl);
    blobCache.delete(key);
  });
}

// Start cleanup on module load
if (typeof window !== 'undefined') {
  startCacheCleanup();

  // Cleanup on page unload
  window.addEventListener('beforeunload', () => {
    if (cleanupTimer) {
      clearInterval(cleanupTimer);
      cleanupTimer = null;
    }
  });
}

/**
 * Convert an authenticated API URL to a data URL blob for use in img tags
 * This ensures the auth token is sent with the request
 * Shared cache prevents duplicate requests across all blob types
 *
 * @param imageUrl - URL to load
 * @param asThumbnail - If true, generate and cache a thumbnail instead of the full image (for non-GIF images)
 * @param mimeType - Optional MIME type to determine caching strategy
 * @param isEncrypted - Whether the image is encrypted
 * @param recipientPublicKey - Public key for decryption
 * @returns The blob URL or null
 */
export async function loadImageAsBlob(
  imageUrl: string | null | undefined,
  asThumbnail: boolean = false,
  mimeType?: string,
  isEncrypted: boolean = false,
  recipientPublicKey?: string,
): Promise<string | null> {
  if (!imageUrl) return null;

  // Generate cache key based on whether it's a thumbnail or not
  const cacheKey = asThumbnail ? `${imageUrl}#thumbnail` : imageUrl;

  // Check cache first
  const cached = blobCache.get(cacheKey);
  if (cached) {
    // Update access count for LRU
    cached.accessCount++;
    return cached.blobUrl;
  }

  // Return pending request if already fetching
  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey)!;
  }

  try {
    // If it's already a full URL or doesn't need auth, return as-is
    if (imageUrl.startsWith('http') && !imageUrl.includes('localhost')) {
      return imageUrl;
    }

    // Create new fetch request
    const fetchPromise = (async () => {
      try {
        // For local API uploads, fetch with auth headers via the API client
        // Global axios interceptor normalizes /api prefixes automatically
        const response = await api.get(imageUrl, {
          responseType: 'blob',
        });

        let blob = response.data;
        if (isEncrypted && recipientPublicKey) {
          const secretKey = encryption.getSecretKey();
          if (secretKey) {
            const arrayBuf = await blob.arrayBuffer();
            const decryptedBlob = encryption.decryptFile(arrayBuf, recipientPublicKey, secretKey);
            if (decryptedBlob) {
              blob = decryptedBlob;
            } else {
              throw new Error('Failed to decrypt image');
            }
          }
        }

        // Generate thumbnail if requested and applicable
        if (asThumbnail && mimeType) {
          // Only generate thumbnails for non-GIF images
          if (shouldCacheThumbnailOnly(mimeType) && !isGif(mimeType)) {
            try {
              blob = await createImageThumbnail(blob, 300, 300);
            } catch (error) {
              console.warn('Failed to generate thumbnail, using original:', error);
              // Fall back to original if thumbnail generation fails
            }
          }
        }

        // Convert blob to object URL
        const blobUrl = URL.createObjectURL(blob);

        // Enforce max entries limit BEFORE adding the new item
        if (blobCache.size >= CACHE_CONFIG.maxEntries) {
          evictLRU();
        }

        // Cache the blob URL with metadata
        blobCache.set(cacheKey, {
          blobUrl,
          timestamp: Date.now(),
          accessCount: 1,
          isThumbnail: asThumbnail,
        });

        return blobUrl;
      } catch (error) {
        console.error('Error loading image:', error);
        throw error;
      } finally {
        pendingRequests.delete(cacheKey);
      }
    })();

    pendingRequests.set(cacheKey, fetchPromise);
    return fetchPromise;
  } catch (error) {
    console.error('Error loading image:', error);
    return null;
  }
}

/**
 * Get avatar URL with proper auth handling
 * Falls back to initials if image fails to load
 *
 * @param avatarUrl - URL of avatar
 * @returns The avatar blob URL or null
 */
export async function getAvatarUrl(avatarUrl: string | null | undefined): Promise<string | null> {
  if (!avatarUrl) return null;

  // For local paths (starting with /), load with auth via blob cache
  // External URLs (http/https) are returned as-is by loadImageAsBlob
  return loadImageAsBlob(avatarUrl);
}

/**
 * Clear the blob cache and revoke all object URLs
 * Call this on logout or when you need to free memory
 */
export function clearBlobCache(): void {
  blobCache.forEach((entry) => {
    URL.revokeObjectURL(entry.blobUrl);
  });
  blobCache.clear();
  pendingRequests.clear();
}

/**
 * Clear a specific blob from cache
 */
export function clearBlobFromCache(url: string): void {
  const entry = blobCache.get(url);
  if (entry) {
    URL.revokeObjectURL(entry.blobUrl);
    blobCache.delete(url);
  }
}

/**
 * Get cache statistics for monitoring
 */
export function getBlobCacheStats() {
  return {
    size: blobCache.size,
    maxEntries: CACHE_CONFIG.maxEntries,
    maxAge: CACHE_CONFIG.maxAge,
    entries: Array.from(blobCache.entries()).map(([url, entry]) => ({
      url,
      age: Date.now() - entry.timestamp,
      accessCount: entry.accessCount,
    })),
  };
}
